<template>
  <div
    ref="groupRef"
    class="lumi-choice-group"
    :class="{ 'lumi-choice-group--tiles': tiles }"
    role="radiogroup"
    :aria-label="label"
    :data-testid="testid"
  >
    <button
      v-for="(option, index) in options"
      :key="option.id"
      type="button"
      role="radio"
      class="lumi-choice-group__option"
      :class="{ 'lumi-choice-group__option--tile': tiles, 'lumi-choice-group__option--selected': tiles && option.id === modelValue }"
      :aria-checked="option.id === modelValue"
      :aria-labelledby="tiles ? `${uid}-${index}-title` : undefined"
      :aria-describedby="tiles && option.description ? `${uid}-${index}-desc` : undefined"
      :tabindex="option.id === modelValue || (!hasValue && index === 0) ? 0 : -1"
      :data-testid="option.testid"
      @click="select(index)"
      @keydown="onKeydown"
    >
      <template v-if="tiles">
        <span class="lumi-choice-group__dot" aria-hidden="true" />
        <span class="lumi-choice-group__text">
          <span :id="`${uid}-${index}-title`" class="lumi-choice-group__title">{{ option.label }}</span>
          <span v-if="option.description" :id="`${uid}-${index}-desc`" class="lumi-choice-group__desc">{{ option.description }}</span>
        </span>
      </template>
      <Chip v-else :selected="option.id === modelValue">{{ option.label }}</Chip>
    </button>
  </div>
</template>

<script setup lang="ts">
/**
 * A choice that is SUBMITTED (a council answer, a hearing answer, cancel-or-reschedule inside
 * a dialog): a radio group, not a view switch — `Tabs` is for what changes what is shown. Each
 * option is a press target around the lib `Chip` (the lib's own pattern for a clickable pill),
 * with the APG radiogroup keyboard contract: one roving tab stop, Arrow keys move AND choose,
 * Home/End jump to the ends.
 *
 * `variant="tiles"` is the same radiogroup drawn as option tiles — a bordered card per option
 * with a radio dot, the label as its title and an optional `description` (the consequence of
 * choosing it) under it. The accessible name stays the title alone; the description is the
 * radio's `aria-describedby`.
 */
import { computed, ref, useId } from 'vue';
import Chip from '../display/Chip.vue';

export type ChoiceOption = { id: string; label: string; testid?: string; description?: string };

const props = defineProps<{
  options: ChoiceOption[];
  modelValue: string;
  /** Accessible name of the group. */
  label?: string;
  testid?: string;
  /** `chips` (default): pill options in a row. `tiles`: stacked option tiles that carry a description. */
  variant?: 'chips' | 'tiles';
}>();
const emit = defineEmits<{ (e: 'update:modelValue', id: string): void }>();

const uid = useId();
const tiles = computed(() => props.variant === 'tiles');
const groupRef = ref<HTMLElement | null>(null);
const hasValue = computed(() => props.options.some((o) => o.id === props.modelValue));

function select(index: number) {
  const option = props.options[index];
  if (option) emit('update:modelValue', option.id);
}

function onKeydown(event: KeyboardEvent) {
  const count = props.options.length;
  if (count === 0) return;
  const found = props.options.findIndex((o) => o.id === props.modelValue);
  const index = found === -1 ? 0 : found;
  let next: number;
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      next = (index + 1) % count;
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      next = (index - 1 + count) % count;
      break;
    case 'Home':
      next = 0;
      break;
    case 'End':
      next = count - 1;
      break;
    default:
      return;
  }
  event.preventDefault();
  select(next);
  groupRef.value?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus();
}
</script>

<style>
.lumi-choice-group {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}
.lumi-choice-group__option {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  cursor: pointer;
  border-radius: var(--radius-pill);
}
.lumi-choice-group--tiles {
  flex-direction: column;
  flex-wrap: nowrap;
}
.lumi-choice-group__option--tile {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  width: 100%;
  min-width: 0;
  padding: var(--space-3);
  text-align: left;
  color: var(--text-heading);
  background: var(--surface-card);
  border: 1px solid var(--field-border);
  border-radius: var(--radius-lg);
}
.lumi-choice-group__option--tile:hover {
  border-color: var(--info);
}
.lumi-choice-group__option--tile:focus-visible {
  outline: 2px solid var(--info);
  outline-offset: 2px;
}
.lumi-choice-group__option--selected,
.lumi-choice-group__option--selected:hover {
  background: var(--selected-bg);
  border-color: var(--info);
}
.lumi-choice-group__dot {
  flex: none;
  box-sizing: border-box;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  border: 1.5px solid var(--text-tertiary);
  border-radius: var(--radius-pill);
}
.lumi-choice-group__option--selected .lumi-choice-group__dot {
  border-color: var(--info);
  background: radial-gradient(circle, var(--info) 0 4px, transparent 4.5px);
}
.lumi-choice-group__text {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}
.lumi-choice-group__title {
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--text-heading);
}
.lumi-choice-group__desc {
  font-size: var(--text-xs);
  line-height: 1.45;
  color: var(--text-secondary);
  overflow-wrap: anywhere;
}
.lumi-choice-group__option--selected .lumi-choice-group__desc {
  color: var(--text-heading);
}
</style>
